import {defineStore} from "pinia";
import {OrganizationService} from "@/javascript/services/organization.service";
import {UserService} from "@/javascript/services/user.service";
import {DonorService} from "@/javascript/services/donor.service";
import {DonationService} from "../services/donation.service";
import {ReportService} from "../services/report.service";
import {useUserStore} from "./user";

export const useDonorStore = defineStore('donor', {
  state: () => ({
    // donorService: new DonorService(),
    donorService: null,
    donationService: null,
    organizationService: new OrganizationService(),
    reportService: null,
    userService: new UserService(),
    modelName: "Donor",
    // these are for analytic events
    traceableType: "Donor",
    traceableName: "donor",
    traceableAssociation: "Donation",

    organizationId: null,
    loading: true,
    donors: [],
    organizations: [],
    currentOrganization: "null",
    currentRecord: {},
    loadingRecord: true,
    loadingAssociationRecords: true,
    permissions: {},
    errors: {},
    generatingDonorReport: false,
    donorReportId: null,
    meta: {
      page: 1,
      pages: 1,
      limit: 10
    },
    associationMeta: {
      page: 1,
      pages: 1,
      limit: 10
    },
    filter: {
      sort: {
        key: "created_at",
        order: "asc"
      },
      search: {
        key: "user_first_name_or_user_last_name_or_user_full_name_or_user_email_cont", // Ransack search query, add more here to expand the search results
        term: null
      }
    },
  }),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.donorService = new DonorService(id);
        this.donationService = new DonationService(id);
        this.reportService = new ReportService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },
    async loadData() {
      try {
        await this.getDonors();
      } catch (e) {
        console.error("Error loading data", e);
      }
    },
    /**
     * This gets the donor and associated donations
     * @param id
     * @returns {Promise<void>}
     */
    async getDonor(id) {
      this.loadingRecord = true;
      this.donations = [];
      return this.donorService.show(id)
        .then((res) => {
          this.currentRecord = res.record;
          this.currentRecord.notes = this.currentRecord.notes || '';
          this.currentRecord.date_of_birth = this.currentRecord.date_of_birth || '';
          this.permissions = res.permissions;
          this.loadingRecord = false;

          return this.getDonorDonations();
        })
        .catch((e) => {
          console.error('Error loading donor', e);
        });
    },

    getDonorDonations() {
      this.loadingAssociationRecords = true;
      let params = new URLSearchParams();
      params.set('query[status_eq]', 'completed');
      params.set('query[s]', 'created_at desc');
      params.set('query[user_id_eq]', this.currentRecord.user_id);

      return this.donationService.paginate(this.associationMeta.page, params).then((res) => {
        this.loadingAssociationRecords = false;
        this.donations = res.data;
        this.associationMeta = res.meta;
        return true;
      });
    },

    getDonors() {
      this.loading = true;
      let params = new URLSearchParams();

      //Sets the page to the current one you are on
      return this.donorService.paginate(this.meta.page, params, this.filter).then((res) => {
        this.donors = res?.data ?? [];
        this.meta = res?.meta ?? this.meta;
      }).catch((e) => {
        console.warn("Error fetching donors", e);
      }).finally(() => {
        this.loading = false;
        return true;
      });
    },

    // Sets the Ransack sort terms based on the passed key
    // This gets passed to index method to sort the paginated results
    sort(key) {
      if (this.filter.sort.key === key) {
        // If the passed key is the current key, flip the current sort order
        this.filter.sort.order = this.filter.sort.order === 'asc' ? 'desc' : 'asc';
      } else {
        // Otherwise set the new key and order by Ascending
        this.filter.sort.key = key;
        this.filter.sort.order = 'asc';
      }

      this.getDonors();
    },
    // TODO move these into a common parent store!

    search(term) {
      this.filter.search.term = term;
      this.meta.page = 1; //this makes it paginate from page 1 not just current page (resets pagination)
      this.getDonors();
    },

    clearSearch() {
      this.filter.search.term = null; //clear the search filters
      this.meta.page = 1; //reloads page to one
      this.getDonors(); //refreshes donors
    },

    filterRecords(key, value) {
      this.filter.filters[key] = value;
      this.getDonors();
    },

    /**
     * Method used in generic Dashboard Show component to load the record.
     * @param id
     */
    async getRecord(id) {
      await this.getDonor(id);
    },
    validate() {
      return true;
    },
    async updateRecord(record) {
      const id = record.id;
      const params = { donor: { notes: record.notes, date_of_birth: record.date_of_birth } };
      await this.donorService.update(id, params);
      return this.getDonor(id);
    },
    nextPage() {
      this.meta.page++;
      this.getDonors();
    },
    prevPage() {
      this.meta.page--;
      this.getDonors();
    },
    moveToPage(page) {
      this.meta.page = page;
      this.getDonors();
    },

    nextAssociationPage() {
      this.associationMeta.page++;
      this.getDonorDonations();
    },
    prevAssociationPage() {
      this.associationMeta.page--;
      this.getDonorDonations();
    },
    moveToAssociationPage(page) {
      this.associationMeta.page = page;
      this.getDonorDonations();
    },
    resendInvitationEmail(userId) {
      this.userService.resendInvitationEmail(userId);
    }
  },

  getters: {
    getCurrentMemberDonations(state) {
      if (state.donations) {
        return state.donations;
      } else {
        return [];
      }
    },
    /**
     * Method used in generic Dashboard Index component.
     * Returns an object containing the index table data.
     * @param state
     */
    getIndexTableData(state) {
      let labels = [
        {label: 'First Name', key: 'f_name', ransackKey: "user_first_name", sort: true, search: true},
        {label: 'Last Name', key: 'l_name', ransackKey: "user_last_name", sort: true, search: true},
        {label: 'Email', key: 'email', ransackKey: 'user_email', sort: true, search: true},
        {label: 'Household', key: 'household_name', sort: false, search: false},
        // { label: 'Gifts', key: 'all_time_donations', format: 'money',  sort: false, search: false }
      ];
      if (state.donors.length > 0) {
        let donors = state.donors.map((donor) => {
          return {
            id: donor.id,
            f_name: donor.user_details.f_name,
            l_name: donor.user_details.l_name,
            email: donor.user_details.email,
            all_time_donations: donor.all_time_donations,
            household_name: (() => {
              if (!donor.household) return '—';
              const userStore = useUserStore();
              const h = donor.household;
              // Does org have a subdomain url?
              if (userStore.userScopedToSubdomain) {
                return { format: 'link', value: h.name, route: { name: 'client-dashboard-scoped', params: { panel: 'households', id: h.id } } };
              }
              return { format: 'link', value: h.name, route: { name: 'client-dashboard', params: { org: userStore.currentOrganization?.slug, panel: 'households', id: h.id } } };
            })()
          };
        });
        return {
          labels: labels,
          rows: donors
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object containing the record table data.
     * @param state
     */
    getShowTableData(state) {
      let labels = [
        'label',
        'data',
      ];
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = [
          {
            label: 'Full Name',
            data: `${state.currentRecord.user_details.f_name} ${state.currentRecord.user_details.l_name}`
          },
          {
            label: 'Date of Birth',
            data: state.currentRecord.date_of_birth || '-'
          },
          {
            label: 'Email',
            data: state.currentRecord.user_details.email
          },
          {
            label: 'Household',
            data: (() => {
              if (!state.currentRecord.household) return '—';
              const userStore = useUserStore();
              const house = state.currentRecord.household;
              // Does org have a subdomain url?
              if (userStore.userScopedToSubdomain) {
                return { format: 'link', value: house.name, route: { name: 'client-dashboard-scoped', params: { panel: 'households', id: house.id } } };
              }
              return { format: 'link', value: house.name, route: { name: 'client-dashboard', params: { org: userStore.currentOrganization?.slug, panel: 'households', id: house.id } } };
            })()
          },
          // TODO removing this due to scoping issues with org tree, plus customer feedback.
          // This endpoint should be getting user records instead of donor records
          // {
          //   label: 'Total Gifts',
          //   data: state.currentRecord.successful_donations_count
          // },
          // {
          //   label: 'Total Dollars',
          //   data: {
          //     format: 'money',
          //     value: state.currentRecord.all_time_donations
          //   }
          // },
          // {
          //   label: 'Organization',
          //   data: state.currentRecord.organization_name
          // }
          {
            label: 'Notes',
            key: 'notes',
            format: 'multiline',
            data: state.currentRecord.notes || ''
          },
        ];
        return {
          labels: labels,
          rows: rows
        };
      } else {
        return {
          labels: labels,
          rows: []
        };
      }
    },
    getEditingTableData(state) {
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        return [
          {label: 'Full Name', key: 'full_name', input: 'readonly', data: `${state.currentRecord.user_details.f_name} ${state.currentRecord.user_details.l_name}`},
          {label: 'Email', key: 'email', input: 'readonly', data: state.currentRecord.user_details.email},
          {label: 'Date of Birth', key: 'date_of_birth', input: 'date', date_only: true},
          {label: 'Notes', key: 'notes', input: 'textarea', rows: 8},
        ];
      }
      return [];
    },
    /**
     * Method used in generic Dashboard Show component.
     * Returns an object with each key being the dashboard panel name of an association,
     *  and each value being the table data for that association.
     * @param state
     */
    getAssociationTableData(state) {
      let labels = [
        {label: 'Amount', key: 'amount', format: 'money'},
        {label: 'Status', key: 'status'},
        {label: 'Date', key: 'created_at', format: 'date'},
        {label: 'Designation', key: 'target_name', format: 'string'},
        {label: 'Organization', key: 'organization_name', format: 'string'},
      ];
      if (state.currentRecord && Object.hasOwn(state.currentRecord, 'id')) {
        let rows = state.getCurrentMemberDonations.map((donation) => {
          donation.status = String(donation.status).charAt(0).toUpperCase() + String(donation.status).slice(1);
          return donation;
        });
        return {
          gifts: {
            labels: labels,
            rows: rows
          }
        };
      } else {
        return {
          gifts: {
            labels: labels,
            rows: []
          }
        };
      }
    }
  }
});
